import { AiQueryResponse, ChatEntry, SendingChatEntry } from '@/models.ts'
import useGlobalStore from '@/useGlobalStore.ts'
import { getTimeBasedRandomString } from '@/lib/utils.ts'
import ChatList from '@/components/chat-list.tsx'
import api from '@/api.ts'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { dispatch } from '@/useBus.ts'
import * as Sentry from '@sentry/react'

const PageChat = ({ className }: { className: string }) => {
	const { t } = useTranslation()
	const {
		user,
		activeChat,
		setActiveChat,
		appLanguageCode,
		setLatestNewChat,
		chatEntries,
		addChatEntry,
		removeChatEntryByFieldName,
		setIsAwaitingAiResponse,
		learningLanguageCode,
		loadChatHistoryList,
		chatHistoryList,
		setLastActiveChatTappedTimestamp,
	} = useGlobalStore()

	const fetchBlob = async (uri: string) => {
		const response = await fetch(uri)
		return await response.blob()
	}

	const sendMessage = async (newMessage: SendingChatEntry) => {
		if (user === null || user === undefined || !user.id) {
			dispatch('show-auth-dialog')
			return
		}

		const existingTs = useGlobalStore.getState().lastActiveChatTappedTimestamp
		const tapTs = existingTs ?? Date.now()
		if (existingTs == null) setLastActiveChatTappedTimestamp(tapTs)

		const clientId = `${getTimeBasedRandomString()}ch${tapTs}`

		const formData = new FormData()
		for (const image of newMessage.images) {
			const imageBlob = await fetchBlob(image.data)
			formData.append('images[]', imageBlob, image.name)
		}
		formData.append('query', newMessage.text)
		formData.append('client_id', clientId)
		formData.append('learning_language_code', learningLanguageCode)
		formData.append('app_language_code', appLanguageCode)

		const newEntry: ChatEntry = {
			id: undefined,
			client_id: clientId,
			user_id: user!.id,
			// @ts-ignore
			message: {
				text: newMessage.text,
				attachments: {
					images: newMessage?.images?.map((x) => x?.data),
				},
			},
			title: '',
			created_at: new Date().toISOString(),
			is_loading: false,
		}
		addChatEntry(newEntry)

		await api(`/v1/chat/query/${activeChat?.id ?? ''}`, {
			method: 'POST',
			body: formData,
		})
			.catch((x) => {
				dispatch('set-chat-message', newMessage.text)
				toast(x.data?.message ?? t('Something went wrong'))
				removeChatEntryByFieldName('client_id', clientId)
			})
			.then(async (chatQueryData) => {
				if (chatQueryData !== null && chatQueryData?.id) {
					if (activeChat == null) {
						setActiveChat(chatQueryData.chat)
						setLatestNewChat(chatQueryData.chat)
					}
					setIsAwaitingAiResponse(true)
					const mainResponseClientId = getTimeBasedRandomString()
					// @ts-ignore
					addChatEntry({
						// @ts-ignore
						message: {
							text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
						},
						client_id: mainResponseClientId,
						is_loading: true,
						type: 'text',
					})

					let aiQueryFailedAfterRetry = false

					const formData = new FormData()
					formData.append('client_id', `${getTimeBasedRandomString()}ch${tapTs}`)

					let aiQueryData: AiQueryResponse | null = null
					aiQueryData = await api(`/v1/chat/query-ai/${chatQueryData.id}`, {
						method: 'POST',
						body: formData,
					}).catch(async () => {
						aiQueryData = await api(`/v1/chat/query-ai/${chatQueryData.id}`, {
							method: 'POST',
						}).catch((x) => {
							aiQueryFailedAfterRetry = true
							toast(x.data?.message ?? t('Something went wrong'))
							removeChatEntryByFieldName('client_id', mainResponseClientId)
							Sentry.captureException(x)
						})
					})

					setIsAwaitingAiResponse(false)

					if (
						aiQueryData === null ||
						aiQueryData?.response_entries === undefined ||
						aiQueryData?.response_entries.length <= 0
					) {
						if (!aiQueryFailedAfterRetry) {
							toast(t('Something went wrong, restart LingoFlow'))
						}
						return
					}

					const lastMessage = aiQueryData?.response_entries[aiQueryData.response_entries.length - 1]

					if (chatHistoryList.find((x) => x.id === chatQueryData?.chat?.id) === undefined) {
						await loadChatHistoryList()
					}

					const currentTs = useGlobalStore.getState().lastActiveChatTappedTimestamp
					if (!currentTs || !lastMessage?.client_id?.endsWith(`ch${currentTs}`)) {
						return
					}

					removeChatEntryByFieldName('client_id', mainResponseClientId)

					setActiveChat(aiQueryData.chat)
					for (const entry of aiQueryData.response_entries) {
						addChatEntry(entry)
					}

					const chatLearningsData = await api(
						`/v1/library/check-for-chat-learnings/${lastMessage.ai_chat_learning_id}`,
						{
							method: 'GET',
						},
					)

					for (const entry of chatLearningsData.library_entries) {
						addChatEntry(entry)
					}
				}
			})
	}

	return (
		<div className={className}>
			<ChatList chat={activeChat} entries={chatEntries} sendMessage={sendMessage} />
		</div>
	)
}

export default PageChat
